import { NgModule, Injectable, LOCALE_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ServiceWorkerModule } from '@angular/service-worker';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient, HttpClientModule, HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HTTP_INTERCEPTORS, HttpErrorResponse } from '@angular/common/http';
import { NgbModule, NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { map, catchError, switchMap, filter, take } from 'rxjs/operators';
import { throwError, Observable, BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { Injector, APP_INITIALIZER } from '@angular/core';
import { LOCATION_INITIALIZED, DecimalPipe } from '@angular/common';
import { AppSettings } from './shared/app-settings';
import { AppRoutingModule } from './app-routing.module';
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import {
  MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration,
  MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration, MsalRedirectComponent
} from '@azure/msal-angular';
import { registerLocaleData } from '@angular/common';
import localeNb from '@angular/common/locales/nb';
registerLocaleData(localeNb);

import { environment } from 'src/environments/environment';
import { AppComponent } from './app.component';
import { MenuComponent } from './components/menu/menu.component';
import { ToastController } from './components/child-components/toast-controller/toast-controller.component';
import { PWAInstallPromptComponent } from './shared/pwa-install-prompt/pwa-install-prompt.component';
import { UpdateNotificationComponent } from './shared/components/update-notification/update-notification.component';
import { DomainManagementComponent } from './components/system-admin/domain-management/domain-management.component';
import { ModuleManagementComponent } from './components/system-admin/module-management/module-management.component';
import { NotificationManagementComponent } from './components/system-admin/notification-management/notification-management.component';
import { IntegrationTypeManagementComponent } from './components/system-admin/integration-type-management/integration-type-management.component';
import { UserAccountManagementComponent } from './components/system-admin/user-account-management/user-account-management.component';
import { SystemAdminGuard } from './services/system-admin.guard';
import { ToastService } from './services/toast-service';
import { HomeService } from './components/home/home.service';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { ClarityService } from './services/clarity.service';
import { NotificationCenterModule } from './components/notification-center/notification-center.module';
import { BugReportModule } from './components/bug-report/bug-report.module';
import { QuickCaptureModule } from './components/quick-capture/quick-capture.module';
import { ConfigService } from './services/config.service';
import { SharedModule } from './shared/shared.module';
import { TenantSelectorComponent } from './components/shared/tenant-selector/tenant-selector.component';
import { TenantSelectorHeaderComponent } from './components/shared/tenant-selector-header/tenant-selector-header.component';
import { TenantGroupManagementComponent } from './components/system-admin/tenant-group-management/tenant-group-management.component';
import { CreateGroupModalComponent } from './components/system-admin/tenant-group-management/create-group-modal/create-group-modal.component';
import { EditGroupModalComponent } from './components/system-admin/tenant-group-management/edit-group-modal/edit-group-modal.component';
import { EmployeeTenantAccessComponent } from './components/setting/employee-tenant-access/employee-tenant-access.component';
import { GrantAccessModalComponent } from './components/setting/employee-tenant-access/grant-access-modal/grant-access-modal.component';
import { OAuthSettingsModalComponent } from './components/system-admin/domain-management/oauth-settings-modal/oauth-settings-modal.component';
import { IntegrationMonitorComponent } from './components/system-admin/integration-monitor/integration-monitor.component';
import { ApiErrorLogComponent } from './components/system-admin/api-error-log/api-error-log.component';
import { EportalApiLogComponent } from './components/system-admin/eportal-api-log/eportal-api-log.component';
import { SystemSettingsComponent } from './components/system-admin/system-settings/system-settings.component';
import { NewCustomerWizardComponent } from './components/system-admin/new-customer-wizard/new-customer-wizard.component';
import { KaiPromptsComponent } from './components/setting/system-setting/kai-prompts/kai-prompts.component';
import { GlobalSearchComponent } from './components/menu/global-search/global-search.component';
import { SwitchExchangeComponent } from './components/login/switch-exchange/switch-exchange.component';


@Injectable()

export class AngularInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private router: Router, 
    private toastService: ToastService, 
    private translate: TranslateService,
    private http: HttpClient,
    private configService: ConfigService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Add token to request
    request = this.addToken(request);

    // Set Content-Type if not already set
    if (!request.headers.has('Accept')) {
      if (!request.headers.has('Content-Type') && !(request.body instanceof FormData)) {
        request = request.clone({ headers: request.headers.set('Content-Type', 'application/json') });
      }
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !request.url.includes('/Authentication/RefreshToken')) {
          return this.handle401Error(request, next);
        }
        if (error.status === 403) {
          this.toastService.show({
            type: 'error',
            data: [this.translate.instant('General.accessDenied')]
          });
        }
        return throwError(() => error);
      })
    );
  }

  private addToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = localStorage.getItem(AppSettings.StorageKeys.Token);
    if (token) {
      return request.clone({ headers: request.headers.set('Authorization', 'Bearer ' + token) });
    }
    return request;
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const currentToken = localStorage.getItem(AppSettings.StorageKeys.Token);
      if (!currentToken) {
        // No token at all — go to login
        this.isRefreshing = false;
        this.clearSessionAndRedirect();
        return throwError(() => new HttpErrorResponse({ status: 401 }));
      }

      const apiBaseUrl = this.configService.get('apiBaseUrl');
      return this.http.post<any>(`${apiBaseUrl}/Authentication/RefreshToken`, { token: currentToken }).pipe(
        switchMap((response: any) => {
          this.isRefreshing = false;
          if (response?.success && response?.result?.token) {
            const newToken = response.result.token;
            localStorage.setItem(AppSettings.StorageKeys.Token, newToken);
            this.refreshTokenSubject.next(newToken);
            // Retry the original request with the new token
            return next.handle(this.addToken(request));
          } else {
            // Refresh failed — clear session
            this.refreshTokenSubject.next(null);
            this.clearSessionAndRedirect();
            return throwError(() => new HttpErrorResponse({ status: 401 }));
          }
        }),
        catchError((refreshError) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          this.clearSessionAndRedirect();
          return throwError(() => refreshError);
        })
      );
    } else {
      // Another request hit 401 while we're already refreshing — wait for the refresh to complete
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap((token) => {
          return next.handle(this.addToken(request));
        })
      );
    }
  }

  private clearSessionAndRedirect(): void {
    localStorage.removeItem(AppSettings.StorageKeys.Token);
    localStorage.removeItem(AppSettings.StorageKeys.User);
    localStorage.removeItem(AppSettings.StorageKeys.ClientConfig);
    localStorage.removeItem(AppSettings.StorageKeys.TimeConfig);
    localStorage.removeItem(AppSettings.StorageKeys.FinalApproval);
    localStorage.removeItem(AppSettings.StorageKeys.MessageCount);
    localStorage.removeItem(AppSettings.StorageKeys.Menu);
    localStorage.removeItem(AppSettings.StorageKeys.DomainDetails);
    this.router.navigate(['/login']);
  }
}

@NgModule({
  declarations: [
    AppComponent, 
    MenuComponent, 
    ToastController, 
    PWAInstallPromptComponent, 
    UpdateNotificationComponent,
    DomainManagementComponent,
    ModuleManagementComponent,
    NotificationManagementComponent,
    IntegrationTypeManagementComponent,
    UserAccountManagementComponent,
    TenantSelectorComponent,
    TenantSelectorHeaderComponent,
    TenantGroupManagementComponent,
    CreateGroupModalComponent,
    EditGroupModalComponent,
    EmployeeTenantAccessComponent,
    GrantAccessModalComponent,
    OAuthSettingsModalComponent,
    IntegrationMonitorComponent,
    ApiErrorLogComponent,
    EportalApiLogComponent,
    SystemSettingsComponent,
    NewCustomerWizardComponent,
    KaiPromptsComponent,
    GlobalSearchComponent,
    SwitchExchangeComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    NgbModule,
    NgbAccordionModule,
    AppRoutingModule,
    HttpClientModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: (environment as any).production === true,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpTranslateLoader,
        deps: [HttpClient],
      },
    }),
    MsalModule,
    BsDatepickerModule.forRoot(),
    NotificationCenterModule,
    BugReportModule,
    QuickCaptureModule,
    SharedModule,
  ],
  providers: [
    HttpClientModule,
    ToastService,
    HomeService,
    SystemAdminGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AngularInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: configInitializerFactory,
      deps: [ConfigService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, Injector],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [ConfigService],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    DecimalPipe,
    { provide: LOCALE_ID, useValue: 'nb-NO' },
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}

export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function appInitializerFactory(translate: TranslateService, injector: Injector) {
  return () => new Promise<any>((resolve: any) => {
    const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
    locationInitialized.then(() => {
      let user: any = null;
      try {
        user = JSON.parse(localStorage.getItem(AppSettings.StorageKeys.User) ?? 'null');
      } catch {
        user = null;
      }

      const language = typeof user?.userLanguage === 'string' ? user.userLanguage.trim().toLowerCase() : '';
      const langToSet = language !== '' ? language : 'no';
      translate.setDefaultLang(langToSet);
      translate.use(langToSet).subscribe(() => {
        resolve(null);
      });
    });
  });
}

/**
 * Factory function to load runtime configuration before app bootstrap.
 * This ensures config.json is loaded before any services try to access it.
 */
export function configInitializerFactory(configService: ConfigService) {
  return () => configService.load();
}

export function loggerCallback(logLevel: LogLevel, message: string) { }

/**
 * Factory function for MSAL instance with runtime config overrides.
 * Combines default environment settings with runtime config from config.json.
 */
export function MSALInstanceFactory(configService: ConfigService): IPublicClientApplication {
  // Use runtime config if available, otherwise fall back to environment defaults
  const clientId = configService.isLoaded() && configService.get('msalClientId') 
    ? configService.get('msalClientId')! 
    : environment.msalConfig.auth.clientId;
  
  // If tenantId is provided, build authority URL from it (takes precedence over msalAuthority)
  let authority: string;
  if (configService.isLoaded() && configService.get('msalTenantId')) {
    const tenantId = configService.get('msalTenantId')!;
    authority = `https://login.microsoftonline.com/${tenantId}/v2.0/authorize`;
  } else if (configService.isLoaded() && configService.get('msalAuthority')) {
    authority = configService.get('msalAuthority')!;
  } else {
    authority = environment.msalConfig.auth.authority;
  }

  return new PublicClientApplication({
    auth: {
      clientId: clientId,
      authority: authority,
      redirectUri: '/',
      postLogoutRedirectUri: '/'
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes]
    },
    loginFailedRoute: '/'
  };
}
